/** Webenv-specific scenario tools (3 tools). */

import { getScenario, SCENARIOS } from "../../scenarios/index.js";

import type { ScenarioContext } from "../../scenarios/index.js";
import type { ToolResult } from "../types.js";

export interface ScenarioToolContext {
  getScenarioContext(): ScenarioContext;
}

function toToolResult(data: unknown): ToolResult {
  return { content: [{ type: "text", text: JSON.stringify(data, null, 2) }] };
}

function toErrorResult(message: string): ToolResult {
  return {
    content: [{ type: "text", text: JSON.stringify({ error: message }) }],
    isError: true,
  };
}

export async function runScenarioHandler(
  params: Record<string, unknown>,
  ctx: ScenarioToolContext,
): Promise<ToolResult> {
  const scenarioId = params.scenario as string;
  if (!scenarioId) {
    return toErrorResult(
      `scenario is required. Available: ${SCENARIOS.map((s) => s.id).join(", ")}`,
    );
  }
  const scenario = getScenario(scenarioId);
  if (!scenario) {
    return toErrorResult(
      `Unknown scenario "${scenarioId}". Available: ${SCENARIOS.map((s) => s.id).join(", ")}`,
    );
  }
  try {
    await scenario.execute(ctx.getScenarioContext());
    return toToolResult({
      scenario: scenarioId,
      status: "completed",
      description: scenario.description,
    });
  } catch (err) {
    return toErrorResult(
      `Scenario "${scenarioId}" failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function composeScenariosHandler(
  params: Record<string, unknown>,
  ctx: ScenarioToolContext,
): Promise<ToolResult> {
  const scenarioIds = params.scenarios as string[];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!scenarioIds || scenarioIds.length === 0) {
    return toErrorResult("scenarios array is required (list of scenario IDs)");
  }

  const results: { scenario: string; status: string }[] = [];
  for (const id of scenarioIds) {
    const scenario = getScenario(id);
    if (!scenario) {
      results.push({ scenario: id, status: `error: unknown scenario` });
      continue;
    }
    try {
      await scenario.execute(ctx.getScenarioContext());
      results.push({ scenario: id, status: "completed" });
    } catch (err) {
      results.push({
        scenario: id,
        status: `error: ${err instanceof Error ? err.message : String(err)}`,
      });
      break; // Stop on first failure
    }
  }
  return toToolResult({
    results,
    completed: results.filter((r) => r.status === "completed").length,
  });
}

export function createPoolHandler(
  params: Record<string, unknown>,
  ctx: ScenarioToolContext,
): ToolResult {
  const name = params.name as string;
  const symbol = params.symbol as string;
  const initialPrice = params.initialPrice as string;
  if (!name || !symbol) {
    return toErrorResult("name and symbol are required");
  }
  try {
    // This delegates to the scenario context which has access to deploy + chain controls
    const _scenarioCtx = ctx.getScenarioContext();
    return toToolResult({
      status: "pool_creation_initiated",
      name,
      symbol,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      initialPrice: initialPrice ?? "1.0",
      message:
        "Use run_scenario with 'create-pool' scenario for full pool creation",
    });
  } catch (err) {
    return toErrorResult(
      `Pool creation failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** All scenario tool definitions for registration. */
export const SCENARIO_TOOLS = [
  {
    name: "run_scenario",
    description:
      "Execute a named scenario (e.g., high-volume-trading, liquidity-crunch)",
    handler: runScenarioHandler,
  },
  {
    name: "compose_scenarios",
    description: "Run multiple scenarios in sequence with checkpoints",
    handler: composeScenariosHandler,
  },
  {
    name: "create_pool",
    description: "Deploy a new token and create liquidity pools",
    handler: createPoolHandler,
  },
] as const;
